import dayjs from "dayjs";
import { convertAndGetTimeStampInRequiredFormat } from "../../../Helper/DatePicker/DateConverter";
import { isOrganizationJBM } from "../../../Helper/Organization/Functions";
import { ANNOTATION_SOURCE } from "./CommonFunctions";
import { valueFromSource } from "../../DigitalTwinV3/components/Header/headerConfig";

/**
 * Identifier keys Advanced Analysis can select an entity by (the Entity Filter
 * tabs). "batteryID" is not one of them, so a header that shows the battery ID
 * opens Advanced Analysis on the device ID instead.
 */
export const ADVANCED_ANALYSIS_ENTITY_KEYS = ["licensePlateNo", "vin", "deviceID"];

// Legacy links send selectedType "vehicle" for the licence plate.
const LEGACY_VEHICLE_SELECTED_TYPE = "vehicle";

/**
 * Entity type for a decoded Advanced Analysis link: the legacy "vehicle" means
 * the licence plate (Entity Filter tab key "licensePlateNo"); a missing type is
 * "deviceID" as before; anything else is passed through.
 */
export const normalizeSelectedType = (selectedType) => {
    if (!selectedType) return "deviceID";
    return selectedType === LEGACY_VEHICLE_SELECTED_TYPE ? "licensePlateNo" : selectedType;
};

/**
 * One header identifier ({ key, source }) as an Advanced Analysis selection, or
 * null when it has no value or no Advanced Analysis equivalent. Values are read
 * exactly as the Digital Twin header reads them (valueFromSource, source
 * defaulting to batteryInfo), so the two never disagree.
 */
const selectionFromField = (field, sources) => {
    if (!field?.key) return null;
    if (field.key === "batteryID") {
        // No battery ID tab in Advanced Analysis: open on the device ID.
        const deviceID = valueFromSource(sources, { key: "deviceID", source: "batteryInfo" });
        return deviceID === null ? null : { selectedType: "deviceID", selectedTypeId: deviceID };
    }
    if (!ADVANCED_ANALYSIS_ENTITY_KEYS.includes(field.key)) return null;
    const value = valueFromSource(sources, field);
    return value === null ? null : { selectedType: field.key, selectedTypeId: value };
};

/**
 * Default entity for Advanced Analysis opened from the Digital Twin: the
 * header's primary identifier (header.title.primaryDetails), else its fallback,
 * else the entry point's own legacy choice unchanged.
 *
 * @param {object} args
 * @param {object} [args.primaryDetails] - page meta header.title.primaryDetails
 * @param {object} [args.vehicleInfo]
 * @param {object} [args.batteryInfo]
 * @param {object} args.legacy           - { selectedType, selectedTypeId } the caller used before
 * @returns {{ selectedType, selectedTypeId }}
 */
export const resolveAdvancedAnalysisEntity = ({ primaryDetails, vehicleInfo, batteryInfo, legacy }) => {
    const sources = { batteryInfo: batteryInfo || {}, vehicleInfo: vehicleInfo || {} };
    const resolved = selectionFromField(primaryDetails, sources)
        || selectionFromField(primaryDetails?.fallback, sources);
    if (resolved) return resolved;
    return { selectedType: legacy?.selectedType, selectedTypeId: legacy?.selectedTypeId };
};

/**
 * Apply resolveAdvancedAnalysisEntity to built route params (their
 * selectedType / selectedTypeId are the legacy choice).
 */
export const withHeaderPrimaryEntity = (params, { primaryDetails, vehicleInfo, batteryInfo } = {}) => ({
    ...params,
    ...resolveAdvancedAnalysisEntity({
        primaryDetails,
        vehicleInfo,
        batteryInfo,
        legacy: { selectedType: params?.selectedType, selectedTypeId: params?.selectedTypeId },
    }),
});

/**
 * Build the (pre-encryption) route params for opening Advanced Analytics from a
 * faults/alerts listing row. Shared by BMS Faults and Smart Alerts entry points
 * so both produce an identical param shape.
 *
 * The date window spans the incident's trigger time to its resolution time
 * (or today when still active). `eventsSource` tells Advanced Analytics which
 * Events root (bmsfaults / smartalerts) to resolve the auto-loaded events under.
 *
 * @param {object} args
 * @param {object} args.battery         — { batteryID, deviceID, licensePlateNo }
 * @param {object} args.alertIncident   — normalized incident (BMS-fault field names)
 * @param {string} [args.source]        — ANNOTATION_SOURCE value
 * @param {object} [args.header]        — Digital Twin callers only: { primaryDetails,
 *                                        vehicleInfo, batteryInfo } so the default
 *                                        entity follows the DT header identifier
 *                                        (see resolveAdvancedAnalysisEntity)
 * @returns {object} params object to pass to encryptTheParams
 */
export const buildAdvancedAnalysisParams = ({ battery, alertIncident, source = ANNOTATION_SOURCE.BMS_FAULT, header = null }) => {
    const events = alertIncident?.incidentErrorName ? [alertIncident.incidentErrorName] : [];

    const startDate = alertIncident?.customerDBTimeInsert
        ? convertAndGetTimeStampInRequiredFormat(alertIncident.customerDBTimeInsert, "YYYY-MM-DD")
        : null;

    const endDate = alertIncident?.resolveCustomerDBTime
        ? convertAndGetTimeStampInRequiredFormat(alertIncident.resolveCustomerDBTime, "YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD");

    let params = {
        batteryID: battery?.batteryID,
        deviceID: battery?.deviceID,
        selectedType: 'deviceID',
        selectedTypeId: battery?.deviceID,
        events: JSON.stringify(events),
        startDate,
        endDate,
        entityAffectedType: alertIncident?.entityAffected || 'battery',
        entityAffectedId: alertIncident?.entityAffectedID,
        alertMetaID: alertIncident?.alertMetaID ?? null,
        category: alertIncident?.category || null,
        eventsSource: source,
    };

    if (isOrganizationJBM() && battery?.licensePlateNo) {
        params = {
            ...params,
            selectedType: 'vehicle',
            selectedTypeId: battery.licensePlateNo,
        };
    }

    return header ? withHeaderPrimaryEntity(params, header) : params;
};
